import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sun, ShieldAlert, Plus, CalendarHeart } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { PaymentDialog } from "@/components/PaymentDialog";
import { fmtDay, money } from "@/lib/daytime";
import { PassCard } from "./daytime/PassCard";
import { PlayCalendar } from "./daytime/PlayCalendar";
import { BookDayPanel } from "./daytime/BookDayPanel";
import { BuyPassDialog } from "./daytime/BuyPassDialog";
import { RockReadyDialog, PassReadyDialog } from "./daytime/RockReadyDialog";
import { MyPlayDays } from "./daytime/MyPlayDays";
import { AddonBookDialog } from "./daytime/AddonBookDialog";
import { MyExtraServices } from "./daytime/MyExtraServices";
import { GiftDialog, RedeemGift } from "./daytime/GiftCard";
import { AddonsGrid, useAddonPrices } from "@/components/AddonsGrid";
import { Gift } from "lucide-react";

export default function DaytimePlay() {
  const [me, setMe] = useState(null);
  const [days, setDays] = useState([]);
  const [selected, setSelected] = useState([]);
  const [focus, setFocus] = useState(null);
  const [kids, setKids] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [payOrder, setPayOrder] = useState(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [passReady, setPassReady] = useState(false);
  const [extras, setExtras] = useState({ upcoming: [], past: [] });
  const [addon, setAddon] = useState(null);
  const [giftOpen, setGiftOpen] = useState(false);
  const addonInfo = useAddonPrices();
  const [params, setParams] = useSearchParams();
  const loc = useLocation();
  const calRef = useRef(null);
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get("/daytime/me").then((r) => setMe(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/daytime/calendar").then((r) => setDays(r.data)).catch(() => {});
    api.get("/addons/mine").then((r) => setExtras(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (params.get("addon")) setAddon({ service: params.get("addon") }); }, [params]);
  useEffect(() => { if (params.get("buy")) setBuyOpen(true); }, [params]);
  const toCal = () => nav("/dashboard/daytime/book");
  useEffect(() => { if (loc.hash === "#calendar") nav("/dashboard/daytime/book", { replace: true }); }, [loc.hash, nav]);

  const tap = async (d) => {
    if (d.status === "full") {
      if (!window.confirm(`${fmtDay(d.date)} is full. Join the waitlist? We'll let you know if a space opens — you won't be charged.`)) return;
      try { await api.post("/daytime/waitlist", { date: d.date, child_ids: kids }); toast.success("You're on the waitlist!"); load(); } catch (e) { toast.error(apiErr(e)); }
      return;
    }
    setFocus(d);
    setSelected(selected.includes(d.date) ? selected.filter((x) => x !== d.date) : [...selected, d.date].sort());
  };

  const book = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/daytime/book", { dates: selected, child_ids: kids });
      setResult(data); setSelected([]); setFocus(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!me) return <p className="text-slate-400">Loading Daytime Play…</p>;
  const active = me.passes.filter((p) => p.effective_status === "active");
  const others = me.passes.filter((p) => p.effective_status !== "active");

  return (
    <div className="space-y-6" data-testid="daytime-dashboard">
      <div className="bg-navy text-white rounded-3xl p-6 grain">
        <p className="text-gold text-xs font-bold tracking-[0.25em] flex items-center gap-2"><Sun className="w-4 h-4" /> DAYTIME PLAY · {me.settings.hours}</p>
        <h1 className="font-display font-black text-3xl mt-1">PLAY. EXPLORE. WORK. CONNECT.</h1>
        <p className="text-white/70 text-sm mt-2 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-coral" /> Advance booking recommended — walk-in spaces are not guaranteed.</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button onClick={toCal} className="rounded-full bg-gold text-navy font-bold h-11" data-testid="book-my-play-days"><CalendarHeart className="w-4 h-4 mr-1" /> BOOK A DAY PASS</Button>
          <Button onClick={() => setBuyOpen(true)} variant="outline" className="rounded-full border-turquoise text-turquoise bg-transparent h-11" data-testid="purchase-pass-btn"><Plus className="w-4 h-4 mr-1" /> PURCHASE A PASS</Button>
        </div>
        {me.credit > 0 && <p className="text-emerald-300 text-sm mt-3" data-testid="daytime-credit">{money(me.credit)} Daytime credit — applied automatically to your next purchase.</p>}
      </div>

      <div>
        <p className="font-display font-extrabold text-navy mb-2">MY MEMBERSHIP / PASS</p>
        {active.length === 0 ? <p className="text-sm text-slate-500" data-testid="no-active-pass">No active pass — book a Day Pass on the calendar, or purchase a Rock Pass or membership.</p> :
          <div className="grid sm:grid-cols-2 gap-4">{active.map((p) => <PassCard key={p.id} p={p} onBook={toCal} />)}</div>}
        {others.length > 0 && <p className="text-xs text-slate-400 mt-2">Past passes: {others.map((p) => `${p.label} (${p.effective_status})`).join(", ")}</p>}
      </div>

      <div ref={calRef} id="calendar" className="hidden" />

      <MyPlayDays me={me} days={days} onChange={load} onPay={(o) => setPayOrder(o)} />

      <div data-testid="extra-help-section">
        <p className="font-display font-extrabold text-navy text-xl">NEED A LITTLE EXTRA HELP?</p>
        <p className="text-sm text-slate-500 mb-3">We've got your Explorer covered.{addonInfo?.member_discount_pct ? ` Monthly & Family members save ${addonInfo.member_discount_pct}%.` : ""}</p>
        <AddonsGrid info={addonInfo} onAdd={(k) => setAddon({ service: k })} />
      </div>

      <MyExtraServices data={extras} onChange={load} onPay={(o) => setPayOrder(o)} />

      <div className="bg-coral/10 border border-coral/20 rounded-3xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid="gift-section">
        <div><p className="font-display font-extrabold text-navy flex items-center gap-2"><Gift className="w-5 h-5 text-coral" /> Gift a Rock Pass</p>
          <p className="text-sm text-slate-600">Send a 5- or 10-Visit Rock Pass to another family by email.</p></div>
        <Button onClick={() => setGiftOpen(true)} className="rounded-full bg-coral text-white font-bold" data-testid="open-gift-dialog">Send a gift</Button>
        <div className="w-full"><RedeemGift initial={params.get("gift")} onRedeemed={() => { setParams({}); load(); }} /></div>
      </div>

      <AddonBookDialog preset={addon} explorers={me.explorers} onClose={() => { setAddon(null); if (params.get("addon")) setParams({}); }}
        onDone={() => { setAddon(null); setParams({}); load(); }} />
      <GiftDialog open={giftOpen} prices={me.settings.prices} onClose={() => setGiftOpen(false)} onOrder={(o) => { setGiftOpen(false); setPayOrder(o); toast.success("Gift created — we'll email it once payment is confirmed"); }} />

      <BuyPassDialog open={buyOpen} initial={params.get("buy")} explorers={me.explorers} settings={me.settings}
        onClose={() => { setBuyOpen(false); if (params.get("buy")) setParams({}); }}
        onDayPass={() => { setBuyOpen(false); setParams({}); toCal(); }}
        onBought={(d) => { setBuyOpen(false); setParams({}); load(); if (d.order) setPayOrder({ ...d.order, after: "pass" }); else setPassReady(true); }} />
      <RockReadyDialog result={result} settings={me.settings} onClose={() => setResult(null)} onPay={() => { setPayOrder(result.order); setResult(null); }}
        onAddon={(k) => { const b = result.bookings[0]; setResult(null); setAddon({ service: k, date: b.date, child_ids: [...new Set(result.bookings.map((x) => x.child_id))], daytime_booking_id: b.id }); }} />
      <PassReadyDialog open={passReady} onClose={() => setPassReady(false)} onChoose={() => { setPassReady(false); toCal(); }} />
      {payOrder && <PaymentDialog order={payOrder} open={!!payOrder} onDone={load}
        onClose={() => { const after = payOrder.after; setPayOrder(null); if (after === "pass") setPassReady(true); }} />}
    </div>
  );
}
